import { Check, CircleX, Search } from "lucide-react";

import { Mark } from "@/components/services/shared/demo-parts";

import type { AdCreative } from "./advertising-demo-copy";

type CcAdPreviewProps = {
  channel: string;
  logo?: string;
  badge?: string;
  stateLabel: string;
  stateVariant: "before" | "after";
  advertiser: string;
  sponsoredLabel: string;
  /** Search query shown above a search ad. */
  query?: string;
  ad: AdCreative;
  chipsLabel: string;
};

/**
 * One ad as a neutral card (not Google's or Meta's own UI): optional search
 * query, "Sponsored · advertiser", headline, text and a call to action, then
 * chips for what goes wrong (before) or what we changed (after), and the
 * outcome. Shares the lead message card's frame and footer styles.
 */
export function CcAdPreview({
  channel,
  logo,
  badge,
  stateLabel,
  stateVariant,
  advertiser,
  sponsoredLabel,
  query,
  ad,
  chipsLabel,
}: CcAdPreviewProps) {
  const StateIcon = stateVariant === "before" ? CircleX : Check;

  return (
    <div className={`cc-lead-message cc-lead-message--${stateVariant} cc-ad-preview`}>
      <div className="cc-auto-flow__bar">
        <span className="cc-auto-flow__name">
          <Mark logo={logo} badge={badge} />
          {channel}
        </span>
        <span className={`cc-auto-flow__state cc-auto-flow__state--${stateVariant}`}>
          <StateIcon className="cc-auto-flow__state-icon" size={11} strokeWidth={2.25} aria-hidden />
          {stateLabel}
        </span>
      </div>

      <div className="cc-lead-message__body">
        {query ? (
          <p className="cc-ad-preview__query">
            <Search size={14} strokeWidth={2} aria-hidden />
            {query}
          </p>
        ) : null}
        <p className="cc-ad-preview__sponsor">
          <span>{sponsoredLabel}</span> · {advertiser}
        </p>
        <p className="cc-ad-preview__headline">{ad.headline}</p>
        <p className="cc-lead-message__text cc-ad-preview__text">{ad.body}</p>
        <span className="cc-ad-preview__cta" aria-hidden="true">
          {ad.cta}
        </span>
      </div>

      <div className="cc-lead-message__footer">
        <p className="cc-lead-message__chips-label">{chipsLabel}</p>
        <ul className="cc-lead-message__chips">
          {ad.chips.map((chip) => (
            <li key={chip.label}>
              <Mark logo={chip.logo} badge={chip.badge} />
              {chip.label}
            </li>
          ))}
        </ul>
        <p className="cc-lead-message__metric">{ad.metric}</p>
      </div>
    </div>
  );
}
